import { useQuery } from '@tanstack/react-query';
import { Link, useRoute } from 'wouter';
import { api } from '../lib/api';
import { useDrafts } from '../lib/drafts';
import { useRuns } from '../lib/pipeline';
import { useSetupOutputs } from '../lib/setup';
import { getSetupReadiness } from '../lib/setupState';
import { useNextStep } from '../lib/nextStep';
import type { Project } from '../lib/projects';
import { ProjectHeader } from '../components/ProjectHeader';

export function DashboardPage() {
  const [, params] = useRoute('/projects/:id');
  const id = params?.id;

  const query = useQuery({
    enabled: !!id,
    queryKey: ['projects', id],
    queryFn: () => api<{ project: Project }>(`/api/projects/${id}`).then((r) => r.project),
  });

  if (!id) return null;

  return (
    <div className="min-h-full">
      <ProjectHeader projectId={id} page="HOME" />
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8">
        {query.isLoading && <p className="font-mono text-xs uppercase tracking-wider text-muted">Loading brand…</p>}
        {query.error && <p className="font-mono text-xs uppercase tracking-wider text-accent-red">Could not load this brand.</p>}
        {query.data && <BrandHome project={query.data} />}
      </main>
    </div>
  );
}

function BrandHome({ project }: { project: Project }) {
  const drafts = useDrafts(project.id, 'all');
  const runs = useRuns(project.id);
  const setup = useSetupOutputs(project.id);
  const readiness = getSetupReadiness(setup.data);

  const posts = drafts.data ?? [];
  const pending = posts.filter((d) => d.status === 'pending').length;
  const approved = posts.filter((d) => d.status === 'approved').length;
  const posted = posts.filter((d) => d.status === 'posted').length;
  const failedRuns = runs.data?.runs.filter((r) => r.status === 'failed').slice(0, 3) ?? [];
  const running = runs.data?.runs.find((r) => r.status === 'running');
  const monthlySpend = runs.data?.monthlySpendUsd ?? 0;

  // Shared "what next" recommendation. Same source of truth as the NextStep
  // rail on every phase page — keeps the Dashboard tile aligned with what the
  // operator sees elsewhere.
  const step = useNextStep(project.id);
  const nextAction = {
    label: step.ctaLabel ?? step.headline ?? 'Open Strategy',
    href: step.ctaHref ?? `/projects/${project.id}/setup`,
    helper: step.helper ?? step.headline,
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[2rem] border border-border-color bg-surface p-7 overflow-hidden relative">
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-primary/15 to-transparent pointer-events-none" />
        <div className="relative grid lg:grid-cols-[1fr_320px] gap-6 items-end">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-accent-cyan mb-3">Brand</p>
            <h1 className="font-display text-2xl md:text-3xl md:text-5xl font-bold tracking-tight">{project.name}</h1>
            <p className="mt-4 text-sm text-muted max-w-2xl leading-relaxed">
              Brief the brand once. Your content team drafts on demand. You review and sign off before anything ships.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Pill>{project.role === 'owner' ? 'Owner' : 'Co-creator'}</Pill>
              <Pill>{project.channels.join(' + ') || 'No channels'}</Pill>
              <Pill>{readiness.ready ? 'Strategy ready' : 'Strategy needs review'}</Pill>
            </div>
          </div>
          <Link href={nextAction.href} className="rounded-2xl border border-accent-cyan/50 bg-accent-cyan/10 p-5 hover:bg-accent-cyan/15 transition-colors group">
            <p className="font-mono text-[10px] uppercase tracking-wider text-accent-cyan">Next best action</p>
            <p className="mt-2 font-display text-2xl font-bold">{nextAction.label}</p>
            <p className="mt-2 text-xs text-muted leading-relaxed">{nextAction.helper}</p>
            <span className="material-symbols-outlined mt-4 text-accent-cyan group-hover:translate-x-1 transition-transform">arrow_forward</span>
          </Link>
        </div>
      </section>

      <section className="grid md:grid-cols-4 gap-3">
        <Metric label="Posts created" value={posts.length} helper={`${posted} published`} />
        <Metric label="Needs review" value={pending} helper={pending ? 'Waiting on you' : 'Queue clear'} tone={pending ? 'amber' : undefined} />
        <Metric label="Ready to publish" value={approved} helper="Approved posts" />
        <Metric label="Team spend" value={`$${monthlySpend.toFixed(4)}`} helper={`Monthly cap $${project.monthlyCostCeilingUsd}`} tone="cyan" />
      </section>

      <section className="grid lg:grid-cols-[1fr_360px] gap-4">
        <div className="rounded-[1.5rem] border border-border-color bg-surface p-5">
          <div className="flex items-baseline justify-between gap-3 mb-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted">Recent posts</p>
              <h2 className="font-display text-2xl font-bold mt-1">Latest created posts</h2>
            </div>
            <Link href={`/projects/${project.id}/posts`} className="text-xs text-accent-cyan hover:underline">View posts →</Link>
          </div>
          {posts.length === 0 ? (
            <Empty title="No posts yet" text="Once your content team produces drafts, they'll appear here for review." />
          ) : (
            <div className="space-y-2">
              {posts.slice(0, 5).map((post) => (
                <Link key={post.id} href={`/projects/${project.id}/posts/${post.id}`} className="block rounded-xl border border-border-color bg-background-dark/70 p-3 hover:border-accent-cyan/60 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{post.topicTitle}</p>
                      <p className="mt-1 text-xs text-muted">{post.format.replace('_', ' ')} · {new Date(post.createdAt).toLocaleString()}</p>
                    </div>
                    <span className={`status-pill ${pillForStatus(post.status)}`}>{post.status}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="rounded-[1.5rem] border border-border-color bg-surface p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted">Brand memory</p>
            <h2 className="font-display text-2xl font-bold mt-1">{readiness.ready ? 'Ready to run' : 'Needs finishing'}</h2>
            {!readiness.ready && <p className="mt-2 text-sm text-muted leading-relaxed">Missing: {readiness.missing.join(', ') || 'strategy outputs'}.</p>}
            <Link href={`/projects/${project.id}/setup`} className="btn-bracket mt-4 inline-block border border-border-color px-3 py-1.5 text-muted hover:text-text-primary hover:border-text-primary">Open strategy</Link>
          </div>

          <div className="rounded-[1.5rem] border border-border-color bg-surface p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted">Engine status</p>
            <h2 className="font-display text-2xl font-bold mt-1">{running ? 'Running now' : 'Idle'}</h2>
            {failedRuns.length > 0 ? (
              <ul className="mt-3 space-y-2 text-xs text-accent-red">
                {failedRuns.map((run) => <li key={run.id} className="break-words">{run.error}</li>)}
              </ul>
            ) : (
              <p className="mt-2 text-sm text-muted">No recent failures.</p>
            )}
            <Link href={`/projects/${project.id}/generator`} className="btn-bracket mt-4 inline-block border border-border-color px-3 py-1.5 text-muted hover:text-text-primary hover:border-text-primary">Open content team</Link>
          </div>
        </aside>
      </section>
    </div>
  );
}

function Metric({ label, value, helper, tone }: { label: string; value: string | number; helper: string; tone?: 'cyan' | 'amber' }) {
  const colour = tone === 'cyan' ? 'text-accent-cyan' : tone === 'amber' ? 'text-accent-amber' : 'text-text-primary';
  return (
    <div className="rounded-2xl border border-border-color bg-surface p-5 overflow-hidden">
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted truncate">{label}</p>
      <p className={`mt-2 font-display text-2xl md:text-4xl font-bold tracking-tight ${colour}`}>{value}</p>
      <p className="mt-2 text-xs text-muted truncate">{helper}</p>
    </div>
  );
}

function Empty({ title, text }: { title: string; text: string }) {
  return (
    <div className="rounded-xl border border-dashed border-border-color p-4 md:p-8 text-center">
      <p className="font-display text-xl font-bold">{title}</p>
      <p className="mt-2 text-sm text-muted">{text}</p>
    </div>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return <span className="rounded-full border border-border-color bg-background-dark/70 px-3 py-1 text-xs text-muted capitalize">{children}</span>;
}

function pillForStatus(status: 'pending' | 'approved' | 'rejected' | 'posted'): string {
  return status === 'approved' || status === 'posted'
    ? 'status-pill-emerald'
    : status === 'rejected'
      ? 'status-pill-red'
      : 'status-pill-amber';
}
